// Dark-safe interpretation of the read-only content-quality analytics responses.
// doGetCall resolves the payload on 200, or { status, errMsg } on non-200, or a raw
// Error on a network reject. The content-quality endpoints are master-flag gated, so a
// 404 means "not enabled" — the caller shows a dark state, never an error.

// Flagged worst-first list. A valid payload is an ARRAY of health objects; 404 → dark;
// anything else (status object, reject, non-array) → error.
export function flaggedResult(res) {
  if (res && res.status === 404) {
    return { state: "dark" };
  }
  if (!Array.isArray(res)) {
    return { state: "error" };
  }
  return { state: "ok", data: res };
}

// Per-question composed health. A valid payload is an object carrying the echoed
// questionId; 404 → dark; other non-200/reject/unexpected shape → error.
export function healthResult(res) {
  if (res && res.status === 404) {
    return { state: "dark" };
  }
  if (!res || res.status || typeof res !== "object" || !("questionId" in res)) {
    return { state: "error" };
  }
  return { state: "ok", data: res };
}

// Category/topic rollup. A valid payload is an ARRAY of category rows; 404 → dark;
// anything else (status object, reject, non-array) → error.
export function categoriesResult(res) {
  if (res && res.status === 404) {
    return { state: "dark" };
  }
  if (!Array.isArray(res)) {
    return { state: "error" };
  }
  return { state: "ok", data: res };
}

// Per-question difficulty trend. A valid payload is an object carrying a windows array.
export function trendResult(res) {
  if (res && res.status === 404) {
    return { state: "dark" };
  }
  if (!res || res.status || typeof res !== "object" || !Array.isArray(res.windows)) {
    return { state: "error" };
  }
  return { state: "ok", data: res };
}

// Per-question distractor analysis. Same dark-safe shape as healthResult; a valid
// payload is an object carrying an options array.
export function distractorResult(res) {
  if (res && res.status === 404) {
    return { state: "dark" };
  }
  if (!res || res.status || typeof res !== "object" || !Array.isArray(res.options)) {
    return { state: "error" };
  }
  return { state: "ok", data: res };
}

// Display helpers — pure, null-safe formatting shared by the table + drill-down.

// A ratio/correlation in [-1,1] or [0,1] → fixed-2 string, or an em dash when undefined
// (null means "cannot compute", never 0).
export function fmtNum(value, digits = 2) {
  return (value === null || value === undefined || Number.isNaN(value))
    ? "—"
    : Number(value).toFixed(digits);
}

// A [0,1] fraction → integer percent, or em dash when undefined.
export function fmtPct(fraction) {
  return (fraction === null || fraction === undefined || Number.isNaN(fraction))
    ? "—"
    : `${Math.round(fraction * 100)}%`;
}

// Short id for dense table cells; keeps the tail (more distinctive) visible.
export function shortId(id, keep = 8) {
  const s = String(id || "");
  return s.length <= keep ? s : `…${s.slice(-keep)}`;
}

// Maps a flag severity to a CSS modifier class suffix (severity may be null = clean).
export function severityClass(severity) {
  switch (severity) {
    case "CRITICAL": return "cq-sev-critical";
    case "WARNING": return "cq-sev-warning";
    case "INFO": return "cq-sev-info";
    default: return "cq-sev-none";
  }
}
